import React from 'react';
//@ts-ignore
import * as styles from './customHeadline.module.css';
import { graphql } from 'gatsby';
//@ts-ignore
import {
    KontentStringValue,
    KontentRadioMultiChoice,
    BrandColor,
} from '../../../../types/KontentProps';
import { getColor, getMultiChoice } from '../../Global/DataUtils';

type HeaderTagType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
type HeadingStyleType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
type HeadlineAlignmentType = 'right' | 'left' | 'center' | undefined;

const TRADEMARK_WITH_WORD = /([^\s\u2122\u00AE]+)(\u2122|\u00AE)/g;

function renderTrademarkParts(text: string): React.ReactNode {
    if (!text) {
        return text;
    }
    if (!text.includes('\u2122') && !text.includes('\u00AE')) {
        return text;
    }
    const parts = text.split(TRADEMARK_WITH_WORD);
    const nodes: React.ReactNode[] = [];
    for (let i = 0; i < parts.length; i += 3) {
        if (parts[i]) {
            nodes.push(parts[i]);
        }
        if (parts[i + 1]) {
            nodes.push(
                <span key={i} className={styles.trademarkGroup}>
                    {parts[i + 1]}
                    <sup className={styles.trademarkMark}>{parts[i + 2]}</sup>
                </span>
            );
        }
    }
    return nodes;
}

interface Props {
    component_anchor_name?: KontentStringValue;
    tag?: KontentRadioMultiChoice<HeaderTagType>;
    style: HeadingStyleType;
    headline_value: KontentStringValue;
    color?: BrandColor;
    overrideStyles?: React.CSSProperties;
    alignment?: KontentRadioMultiChoice<HeadlineAlignmentType>;
    className?: string;
    enable_trademark_styling?: KontentRadioMultiChoice<'yes' | 'no'>;
    elementCodename?: string;
}

const CustomHeadline: React.FC<Props> = (props) => {
    let { tag, style, headline_value, color, alignment, overrideStyles } =
        props;
    const headlineText = headline_value?.value;

    if (!headlineText?.trim()) return null;

    const trademarkEnabled =
        getMultiChoice(props.enable_trademark_styling) === 'yes';
    const formatHeadlineText = (text: string): React.ReactNode =>
        trademarkEnabled ? renderTrademarkParts(text) : text;
    const textColor = getColor(color);
    const HeaderTag = typeof tag != 'string' ? getMultiChoice(tag) : tag;
    const StyleTag = style;

    const headlineAlignment = getMultiChoice(alignment);
    const headlineAlignmentCase = headlineAlignment
        ? headlineAlignment.toLowerCase()
        : null;

    let headlineAlignPosition = setInnerContentPosition(headlineAlignmentCase);
    function htmlHeader(header: any) {
        switch (header) {
            case 'h1':
                return 'h1';
            case 'h2':
                return 'h2';
            case 'h3':
                return 'h3';
            case 'h4':
                return 'h4';
            case 'h5':
                return 'h5';
            case 'h6':
                return 'h6';
            case 'p':
                return 'p';
            default:
                return 'div';
        }
    }

    const componentStyles = overrideStyles
        ? { ...headlineAlignPosition, ...overrideStyles }
        : headlineAlignPosition;

    return (
        <>
            {!headlineText.includes('\n')
                ? React.createElement(
                      htmlHeader(HeaderTag),
                      {
                          style: {
                              color: textColor,
                              style: StyleTag,
                              ...componentStyles,
                          },
                          className: [
                              styles[StyleTag],
                              styles.headlineStyle,
                              props.className,
                          ]
                              .filter(Boolean)
                              .join(' '),
                          ...(props.elementCodename
                              ? {
                                    'data-kontent-element-codename':
                                        props.elementCodename,
                                }
                              : {}),
                      },
                      <>
                          {headlineText.endsWith('*') ? (
                              <>
                                  {formatHeadlineText(
                                      headlineText.slice(0, -1)
                                  )}
                                  <span className={styles.asterisk}>*</span>
                              </>
                          ) : (
                              formatHeadlineText(headlineText)
                          )}
                      </>
                  )
                : headlineText.split('\n').map((text, index) => {
                      if (!text.trim()) return null;

                      return React.createElement(
                          htmlHeader(HeaderTag),
                          {
                              style: {
                                  color: textColor,
                                  style: StyleTag,
                                  ...componentStyles,
                              },
                              className: [
                                  styles[StyleTag],
                                  styles.headlineStyle,
                                  props.className,
                              ]
                                  .filter(Boolean)
                                  .join(' '),
                              id: props?.component_anchor_name?.value
                                  ? props?.component_anchor_name?.value + index
                                  : index,
                              key: index,
                              ...(props.elementCodename
                                  ? {
                                        'data-kontent-element-codename':
                                            props.elementCodename,
                                    }
                                  : {}),
                          },
                          <>
                              {text?.endsWith('*') ? (
                                  <>
                                      {formatHeadlineText(text.slice(0, -1))}
                                      <span className={styles.asterisk}>*</span>
                                  </>
                              ) : (
                                  formatHeadlineText(text ?? '')
                              )}
                          </>
                      );
                  })}
        </>
    );
};

export default CustomHeadline;

function setInnerContentPosition(headlineAlignmentCase: HeadlineAlignmentType) {
    if (headlineAlignmentCase == 'left') {
        return {
            textAlign: 'left',
        } as React.CSSProperties;
    }

    if (headlineAlignmentCase == 'right') {
        return {
            textAlign: 'right',
        } as React.CSSProperties;
    }
    if (headlineAlignmentCase == 'center') {
        return {
            textAlign: 'center',
        } as React.CSSProperties;
    }

    return {};
}
